'use client'

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Box, Button, Link } from '@mui/material'
import { PageHeader } from '@/components/ui/page-header'
import { CreateRecurringScheduleModal } from '@crm-src/components/create-recurring-schedule-modal'
import { customersApi } from '@crm-src/lib/customers-api'
import { InvoiceTabs } from '@crm-src/components/invoice-tabs'
import { RecurringTimeline } from './RecurringTimeline'

/**
 * Recurring Invoices — a single, focused Timeline view.
 *
 * The page is just a forward-looking projection of when every ACTIVE recurring
 * schedule's next invoices are expected to generate: `<RecurringTimeline>`
 * renders its own summary stats (next-30-days / projected count / projected
 * value) above a month-grouped rail of upcoming occurrences. "Plain and simple"
 * on purpose — top area some stats, then the timeline.
 *
 * Browsing + managing the schedules themselves (the full table, pause / resume /
 * cancel / edit) lives on the schedule detail page
 * (`/crm/invoices/schedules/:id`), which every timeline card links to. Creating a
 * new schedule stays here via the header "New Schedule" action.
 */
export default function RecurringInvoicesPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const customerId = searchParams.get('customer_id')

  const [customerName, setCustomerName] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  // Bumped after a schedule is created so the timeline re-fetches its projection.
  const [refreshSignal, setRefreshSignal] = useState(0)

  // When scoped to a single customer (?customer_id), resolve their name for the
  // title + "Back to customer" link. The timeline owns its own projection fetch,
  // so this is the only data this page loads.
  useEffect(() => {
    if (!customerId) {
      setCustomerName(null)
      return
    }
    let cancelled = false
    customersApi
      .getById(customerId)
      .then((res) => {
        if (cancelled) return
        const customer = res.customer as { full_name?: string; company_name?: string } | undefined
        setCustomerName(customer?.full_name || customer?.company_name || null)
      })
      .catch(() => {
        /* fall back to the generic "Customer" title */
      })
    return () => {
      cancelled = true
    }
  }, [customerId])

  const handleCreated = useCallback(() => {
    setRefreshSignal((n) => n + 1)
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Tabs Navigation */}
      <InvoiceTabs />

      <PageHeader
        title={customerId ? `Recurring Invoices for ${customerName || 'Customer'}` : 'Recurring Invoices'}
        description={
          <>
            <Box component="span" sx={{ maxWidth: '48rem', display: 'block' }}>
              {customerId
                ? 'Upcoming invoices projected from the active recurring schedules for this customer.'
                : 'Upcoming invoices projected across every active recurring schedule (weekly, monthly, quarterly, yearly).'}
            </Box>
            {customerId && (
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate(`/crm/customers/${customerId}`)}
                sx={{ fontSize: 12, p: 0, color: 'primary.main' }}
              >
                ← Back to customer
              </Link>
            )}
          </>
        }
        actions={
          <Button variant="contained" onClick={() => setIsCreateModalOpen(true)} startIcon={<Plus size={16} />}>
            New Schedule
          </Button>
        }
      />

      {/* The whole page is the timeline: summary stats on top, then the rail. */}
      <RecurringTimeline customerId={customerId} refreshSignal={refreshSignal} />

      {/* Create Modal — the one schedule mutation that stays on this page. */}
      <CreateRecurringScheduleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreated}
      />
    </Box>
  )
}
